/**
 * Plate-math helpers — pure functions for the in-keypad plate
 * calculator (v9.216). Decoupled from the React component so the
 * decomposition logic can be unit-tested cleanly + reused if a
 * future surface (Dashboard "1RM tool" widget, routine-editor
 * plate preview) wants the same math.
 *
 * ## Plate sets
 *
 * Standard Olympic plate sets per unit. Ordered LARGEST → SMALLEST
 * so greedy decomposition walks them in priority order.
 *
 * KG (most gyms outside US):
 *   25 / 20 / 15 / 10 / 5 / 2.5 / 1.25 kg
 *
 * LB (US gyms):
 *   45 / 35 / 25 / 10 / 5 / 2.5 / 1.25 lb
 *
 * 1.25kg / 1.25lb plates aren't universally stocked but are common
 * enough for fractional-load progression (esp. women's lifting,
 * deload weeks). Included by default; lifters who don't have them
 * just leave count at 0.
 *
 * ## Bar weights
 *
 * Ordered narrow → wide for the cycle UI (smaller weights are more
 * common edge cases; the standard 20kg / 45lb is the default).
 *
 * KG: 20 (standard) / 15 (women's) / 10 (technique) / 7.5 (EZ) / 0
 * LB: 45 (standard) / 35 (women's) / 25 (technique) / 16 (EZ) / 0
 *
 * `0` = bodyweight / dumbbell / machine etc. — the "no bar" option
 * for exercises like assisted dips, lat pulldowns where the user
 * still wants per-side plate math (e.g. a dumbbell with plates
 * loaded). Edge case but supported for completeness.
 */

import type { WeightUnit } from "@/lib/stats";

export const PLATE_SETS: Record<WeightUnit, readonly number[]> = {
  kg: [25, 20, 15, 10, 5, 2.5, 1.25],
  lb: [45, 35, 25, 10, 5, 2.5, 1.25],
};

// v9.257 — dropped the 0-weight option from each unit's cycle.
// Pre-fix the cycle wrapped through 20 → 15 → 10 → 7.5 → 0 → 20…
// (and the LB analogue). The 0-weight case was a "bar-less" mode
// for free-weight calculations without a barbell, but in practice
// nobody used it — it just confused the bar cycle. The new selector
// UI in the barbell SVG centre also reads naturally without it
// (four discrete bar choices, no special-case zero).
export const BAR_WEIGHTS: Record<WeightUnit, readonly number[]> = {
  kg: [20, 15, 10, 7.5],
  lb: [45, 35, 25, 16],
};

/** Default bar weight per unit — the standard Olympic bar. */
export function defaultBarWeight(unit: WeightUnit): number {
  return BAR_WEIGHTS[unit][0]!;
}

/** Cycle to the next bar weight in BAR_WEIGHTS[unit]. Wraps from
 *  the last entry back to the first. Returns the default bar weight
 *  if the current value isn't in the cycle list (defensive — a
 *  legacy stored bar weight that's no longer in the set falls back
 *  cleanly rather than getting stuck). */
export function cycleBarWeight(current: number, unit: WeightUnit): number {
  const list = BAR_WEIGHTS[unit];
  const idx = list.indexOf(current);
  if (idx === -1) return list[0]!;
  return list[(idx + 1) % list.length]!;
}

/** Greedy decomposition of a target weight into per-side plate
 *  counts.
 *
 *  Algorithm:
 *    perSide = (target - bar) / 2
 *    foreach denom in PLATE_SETS[unit] (largest → smallest):
 *      count = floor(perSide / denom)
 *      perSide -= count * denom
 *
 *  Returns:
 *    counts:   one entry per plate denomination, same order as
 *              PLATE_SETS[unit]
 *    total:    bar + 2 × Σ(counts × denoms)  — the EXACT total
 *              the returned counts produce (may differ from
 *              `target` if not perfectly decomposable)
 *    diff:     total - target. Zero when target is exact; non-zero
 *              when greedy can't hit it (e.g. target = 100.6kg
 *              with 1.25kg as the smallest plate → closest is
 *              100.0kg, diff = -0.6).
 *
 *  Negative-target edge case: if target < bar, perSide is
 *  negative; all counts return as 0 and total = bar (the bar
 *  alone is heavier than the target). diff carries the gap.
 *
 *  Floating-point note: the loop uses Math.floor + subtraction
 *  which can accumulate tiny FP error (1.25 + 1.25 + 1.25 +
 *  1.25 = 4.999999999999999 instead of 5.0). We round to 2
 *  decimal places at the end to neutralise that. Plate counts
 *  are integers so no rounding needed there.
 */
export function decomposeTarget(
  target: number,
  bar: number,
  unit: WeightUnit,
): { counts: number[]; total: number; diff: number } {
  const denoms = PLATE_SETS[unit];
  const counts: number[] = denoms.map(() => 0);
  let perSide = (target - bar) / 2;
  if (perSide <= 0) {
    return {
      counts,
      total: bar,
      diff: bar - target,
    };
  }
  for (let i = 0; i < denoms.length; i++) {
    const denom = denoms[i]!;
    const count = Math.floor(perSide / denom + 1e-9); // FP tolerance
    counts[i] = count;
    perSide -= count * denom;
  }
  const total = computeTotal(counts, bar, unit);
  return {
    counts,
    total,
    diff: Math.round((total - target) * 100) / 100,
  };
}

/** Sum plate counts × denominations × 2 + bar weight. Pure helper
 *  used by the React component to live-update the readout as the
 *  user adjusts per-plate counts manually. Rounds to 2 decimal
 *  places to neutralise FP accumulation. */
export function computeTotal(
  counts: readonly number[],
  bar: number,
  unit: WeightUnit,
): number {
  const denoms = PLATE_SETS[unit];
  let perSide = 0;
  for (let i = 0; i < denoms.length; i++) {
    perSide += (counts[i] ?? 0) * (denoms[i] ?? 0);
  }
  return Math.round((bar + 2 * perSide) * 100) / 100;
}

/**
 * v9.222 — "closest" decomposition (Option B).
 *
 * Greedy `decomposeTarget` always lands UNDER the target when the
 * plate set can't represent the exact weight (e.g. 122kg with the
 * standard KG set: greedy gives 120kg, leaving a 2kg gap because
 * 1kg can't be made from any combo of 1.25+). For lifters who want
 * the closest possible LOAD to their target — not always under —
 * `decomposeTargetClosest` compares the greedy UNDER result with
 * an OVER result (greedy + 1 of the smallest plate) and returns
 * whichever has the smaller |diff|.
 *
 * Algorithm:
 *   1. under = decomposeTarget(...)   // greedy, always ≤ target
 *   2. If under.diff === 0 (exact hit), return under.
 *   3. over   = under + 1 of smallest plate (always overshoots by
 *      at most 2× the smallest plate's weight, since greedy left
 *      a remainder < smallest plate by construction)
 *   4. Return whichever has the smaller |diff|. Tie-break: prefer
 *      UNDER (loading less is safer than over-loading; lifters
 *      generally know the "exact-or-less" rule from comp lifting).
 *
 * Example: target = 122 kg, bar = 20 kg, KG plate set.
 *   under = 120 kg (2×25 per side), diff = −2 kg
 *   over  = 122.5 kg (2×25 + 1×1.25 per side), diff = +0.5 kg
 *   |under diff| = 2, |over diff| = 0.5 → return over (122.5).
 *
 * Example: target = 100.3 kg, bar = 20 kg.
 *   under = 100 kg, diff = −0.3 kg
 *   over  = 102.5 kg, diff = +2.2 kg
 *   |under| = 0.3, |over| = 2.2 → return under (100).
 */
export function decomposeTargetClosest(
  target: number,
  bar: number,
  unit: WeightUnit,
): { counts: number[]; total: number; diff: number } {
  const under = decomposeTarget(target, bar, unit);
  if (under.diff === 0) return under;
  if (target <= bar) return under; // can't go below bar weight
  const denoms = PLATE_SETS[unit];
  // Greedy leaves remainder < smallest plate by construction, so
  // the smallest plate is ALWAYS the right "over" addition. Add
  // 1 to that index.
  const smallestIdx = denoms.length - 1;
  const overCounts = [...under.counts];
  overCounts[smallestIdx] = (overCounts[smallestIdx] ?? 0) + 1;
  const overTotal = computeTotal(overCounts, bar, unit);
  const overDiff = Math.round((overTotal - target) * 100) / 100;
  // Tie-break: prefer under (safer). |over| must be STRICTLY less
  // than |under| to flip to over.
  if (Math.abs(overDiff) < Math.abs(under.diff)) {
    return { counts: overCounts, total: overTotal, diff: overDiff };
  }
  return under;
}
